import { useToast } from '@/composables/useToast.js';
import { useRouter } from 'vue-router';

export function useHttpErrorManager() {
    const router = useRouter();
    const handleError = async (error, showToast = false) => {
        let errorFields = {};

        let message = error.message;
        let error_message = '';
        let resourceNotFound = false;

        if (error?.code === 'ERR_OFFLINE_WRITE') {
            error_message = 'You are offline — changes are disabled until the connection returns.';
            errorFields['err_message'] = error_message;
            errorFields['full_message'] = error_message;
            errorFields['code'] = 'ERR_OFFLINE_WRITE';
            if (showToast) {
                useToast().displayToast({ message: error_message, variant: 'warning', duration: 8000 });
            }
            return errorFields;
        }

        // The subscription lock, refused before the request departed — the
        // same posture as an offline write: a warning, not a fault.
        if (error?.code === 'ERR_BILLING_LOCKED') {
            error_message = error.message || 'Your subscription is locked — changes are disabled until the payment is made.';
            errorFields['err_message'] = error_message;
            errorFields['full_message'] = error_message;
            errorFields['code'] = 'ERR_BILLING_LOCKED';
            if (showToast) {
                useToast().displayToast({ message: error_message, variant: 'warning', duration: 8000 });
            }
            return errorFields;
        }

        if (error.response !== undefined && error.response.status !== undefined && error.response.status === 422) {
            let error_set = error.response.data.errors;
            if (error_set) {
                error_set = Object.entries(error_set);
                error_set.forEach((errorItem) => {
                    errorFields[errorItem[0]] = errorItem[1][0];
                });
            }
            error_message = error.response.data.message;
        } else if (error.response !== undefined && error.response.status !== undefined && error.response.status === 404) {
            resourceNotFound = true;
        } else if (error.response !== undefined && error.response.status !== undefined && error.response.data !== undefined) {
            error_message = error.response.data.message;
        }

        if (
            error.response !== undefined &&
            error.response.data !== undefined &&
            error.response.data.message !== undefined &&
            error.response.data.title !== undefined
        ) {
            error_message = error.response.data.message;
        }

        let temp_message = '';
        if (error_message !== undefined && error_message !== null && error_message !== '') {
            errorFields['err_message'] = error_message;
            temp_message = error_message;
        }

        if (message !== undefined && message !== null && message !== '') {
            errorFields['alt_message'] = message;
            temp_message += '\n' + message;
        }

        errorFields['full_message'] = temp_message;

        /*
         * A server fault gets its request reference.
         *
         * Every API response carries X-Request-Id, and the audit log records
         * the request under exactly that id. Putting it on the toast turns
         * "it broke" into something support can look up — the person reads
         * one short code off the screen instead of trying to describe what
         * they were doing at the time.
         *
         * Only on 5xx: a validation error is already explained by its own
         * message, and a reference on every rejected form would be noise.
         */
        const requestId = error?.response?.headers?.['x-request-id'] ?? null;
        const status = error?.response?.status ?? 0;
        if (requestId && status >= 500) {
            errorFields['request_id'] = requestId;
            errorFields['full_message'] = `${temp_message}\nReference: ${requestId}`;
        }

        if (showToast) {
            useToast().displayToast({ message: errorFields['full_message'], variant: 'danger', duration: 8000 });
        }

        if (resourceNotFound) {
            // useRouter() only resolves while a component's setup is running;
            // an error handler invoked from an async callback has none, so
            // report the miss as a toast instead of crashing on the push.
            if (router) {
                await router.push({ name: 'error' });
            } else {
                useToast().displayToast({
                    message: temp_message || 'The requested resource could not be found.',
                    variant: 'danger',
                    duration: 8000,
                });
            }
        }

        return errorFields;
    };

    return {
        handleError,
    };
}
